import type { ReactElement } from "react";
import type { ImportEntity, JobDetail } from "@cx/contracts";
import { ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { dateTime } from "../../format";
import { Link } from "@tanstack/react-router";
import { JOB_KIND, JOB_STATUS, jobHasFile, useJobs, type Job } from "../../jobs";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";

export function JobsPage(): ReactElement {
  const toast = useToast();
  const { api } = useSession();
  const q = useJobs();
  const download = async (j: Job) => {
    try {
      const r = await api<JobDetail>(`/jobs/${j.id}`);
      if (r.downloadUrl) window.location.assign(r.downloadUrl);
      else toast("warning", "O arquivo desta ação não está mais disponível.");
    } catch (e) {
      toast("danger", errorMessage(e));
    }
  };
  return (
    <>
      <ContentHeader title="Gestão de ações assíncronas" breadcrumb={["Preferências", "Ações assíncronas"]} />
      <div className="box card">
        <QueryStatus loading={q.isLoading} error={q.error} />
        {q.isSuccess && (
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th scope="col">Ação</th>
                  <th scope="col">Criada em</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Progresso</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {q.data.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Nenhuma ação ainda.
                    </td>
                  </tr>
                )}
                {q.data.data.map((j) => {
                  const kind = JOB_KIND[j.kind] ?? j.kind;
                  return (
                    <tr key={j.id}>
                      <td>{kind}</td>
                      <td>{dateTime(j.createdAt)}</td>
                      <td>
                        <StatusBadge tone={JOB_STATUS[j.status][0]}>{JOB_STATUS[j.status][1]}</StatusBadge>
                        {j.error && <div className="small text-danger">{j.error}</div>}
                      </td>
                      <td>
                        {j.total ? (
                          <progress
                            value={j.progress}
                            max={j.total}
                            aria-label={`Progresso de ${kind}: ${j.progress} de ${j.total}`}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-end">
                        {j.kind.startsWith("import_") && (
                          <Link
                            to="/preferencias/importacoes"
                            search={{ tipo: j.kind.slice("import_".length) as ImportEntity }}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            Ver importações
                          </Link>
                        )}
                        {j.status === "done" && jobHasFile(j.kind) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            aria-label={`Baixar ${kind} de ${dateTime(j.createdAt)}`}
                            onClick={() => download(j)}
                          >
                            Baixar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
